import { Injectable, inject, isDevMode } from '@angular/core';
import { AuthService } from './auth.service';
import { CurrentUserService } from './current-user.service';
import { MockDataService } from './mock-data.service';
import { ApiDonorResponse } from '../../modules/donation-portal/features/donors/models/donor-management.response';
import { environment } from 'src/environments/environment';

/**
 * Development-only auth service that simulates JWT authentication.
 * Generates a mock JWT token with proper claims for both portals.
 * Replace with real Company SSO integration in production.
 */
@Injectable({ providedIn: 'root' })
export class DevAuthService {
  private readonly authService = inject(AuthService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly mockData = inject(MockDataService);

  private assertDevOnly(): void {
    if (environment.production) {
      throw new Error('DevAuthService must not be used in production. Use real authentication instead.');
    }
  }

  /**
   * Simulate donation portal login — clears any existing session,
   * creates a JWT with staff claims, and sets portal context.
   */
  loginAsDonationPortal(username: string): void {
    this.assertDevOnly();
    // Clear any previous session (e.g., donor JWT)
    this.authService.clearAuthData();

    const session = this.mockData.session;
    const token = this.createMockJwt({
      userid: String(session.staffId),
      companyid: '1',
      email: `${username.toLowerCase()}@brijjworks.com`,
      fullname: session.staffName,
      role: 'staff',
      phonenumber: '(555) 100-0000'
    });

    this.authService.setTokens(token, `refresh-${Date.now()}`, `session-${Date.now()}`);
    this.currentUser.setPortal('donation');
  }

  /**
   * Simulate donor login — clears any existing session,
   * creates a JWT with donor claims, and wires up donor context.
   */
  loginAsDonor(donorIdOrEmail: number | string): void {
    this.assertDevOnly();

    // Clear any previous session (e.g., donation portal JWT)
    this.authService.clearAuthData();

    const donors = this.mockData.donors;
    const donor =
      typeof donorIdOrEmail === 'number'
        ? (donors.find(d => d.id === donorIdOrEmail) ?? donors[0])
        : (donors.find(
            d =>
              d.phone === donorIdOrEmail ||
              d.email?.toLowerCase() === (donorIdOrEmail as string).toLowerCase()
          ) ?? donors[0]);
    const token = this.createMockJwt({
      userid: String(donor.id),
      companyid: '1',
      email: donor.email ?? `donor-${donor.id}@brijjworks.com`,
      fullname: donor.firstName ? `${donor.firstName} ${donor.lastName ?? ''}`.trim() : donor.referenceNumber,
      role: 'donor',
      phonenumber: donor.phone ?? '',
      userOrigin: 'External'
    });

    this.authService.setTokens(token, `refresh-${Date.now()}`, `session-${Date.now()}`);
    this.currentUser.setPortal('donor');
    this.currentUser.setDonorId(donor.id);
  }

  /**
   * Clear simulated auth session.
   */
  logout(): void {
    this.authService.clearAuthData();
  }

  private findDonorById(id: number): ApiDonorResponse {
    return this.mockData.donors.find(d => d.id === id) ?? this.mockData.donors[0];
  }

  /**
   * Create a mock JWT token with standard claims.
   * Uses base64url encoding to create a valid JWT structure that jwt-decode can parse.
   * Token expires in 24 hours.
   */
  private createMockJwt(claims: {
    userid: string;
    companyid: string;
    email: string;
    fullname: string;
    role: string;
    phonenumber: string;
    userOrigin?: string;
  }): string {
    const header = { alg: 'HS256', typ: 'JWT' };
    const now = Math.floor(Date.now() / 1000);
    const payload = {
      sub: claims.userid,
      'custom:userid': claims.userid,
      'custom:companyid': claims.companyid,
      'custom:email': claims.email,
      'custom:fullname': claims.fullname,
      'custom:role': [claims.role],
      'custom:phonenumber': claims.phonenumber,
      'custom:usertype': 'AppUser',
      'custom:userorigin': claims.userOrigin ?? 'Internal',
      'custom:timezonecode': 'UTC',
      'custom:mustchangepasswordonlogin': 'False',
      iss: 'dev-auth-simulator',
      aud: 'donation-app',
      iat: now,
      nbf: now,
      exp: now + 86400 // 24 hours
    };

    const encode = (obj: object): string =>
      btoa(JSON.stringify(obj)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

    return `${encode(header)}.${encode(payload)}.dev-signature`;
  }
}
